import type { LucideIcon } from 'lucide-react';
import { ALL_MODULES_ON, canView, type ModuleKey, type ModuleState, type PermissionObject, type SessionPayload } from '@nest-recruit/shared';
import { COMMAND_MODULES, isRequirementOn } from '../lib/module-map';

// The command palette's index.
//
// 06-qol §1: "Every feature slice registers its own commands into a central
// registry rather than editing one giant file." So this is a plain module-level
// store with no React in it — a feature module calls `registerCommands` at import
// time, gets an unregister function back, and never touches a shared array.
//
// It is also why the palette can hold its 100ms budget: the index is already in
// memory when Ctrl+K is pressed. Nothing here fetches, and nothing may.

export type CommandSection = 'action' | 'recent' | 'navigate';

/** Display order, fixed by 06-qol §1: Actions, then Recent, then Navigate. */
export const COMMAND_SECTIONS: readonly CommandSection[] = ['action', 'recent', 'navigate'];

export const COMMAND_SECTION_LABELS: Readonly<Record<CommandSection, string>> = {
  action: 'Actions',
  recent: 'Recent',
  navigate: 'Navigate',
};

export interface Command {
  /** Stable and namespaced — `job.create`, `nav.candidates`. Also the React key. */
  readonly id: string;
  /** Sentence case (06-qol §11). This is what fuzzy matching runs against. */
  readonly label: string;
  readonly section: CommandSection;
  /**
   * The type, and the parent where there is one: "Candidate — Senior accountant,
   * Venture B" (06-qol §1). Shown beside the label and also matched against, so
   * typing a job name finds the candidates on it.
   */
  readonly context?: string;
  /**
   * The §L23 object this command needs read access to. Results are
   * permission-filtered **before display** — a user must never see a result they
   * cannot open. Omit only for commands everyone may run, such as "Toggle theme".
   */
  readonly permission?: PermissionObject;
  /**
   * A stricter check than `permission`, for when read access is not the same as
   * "this is a place you can go". A panelist can read an interviewee's candidate
   * record from inside their interview (§L23 `S (interviewees only)`), but the
   * candidate directory is not a destination for them — so the Navigate entry uses
   * the module bar's own rule and the two can never disagree.
   */
  readonly canSee?: (session: SessionPayload, modules: ModuleState) => boolean;
  /**
   * R1 (§L28): the switchable module this command belongs to. A command whose module is off is
   * never offered. Feature commands are mapped by id in `lib/module-map.ts` instead.
   */
  readonly module?: ModuleKey;
  /**
   * Screen ids this command is contextual to. A command with screens listed is
   * offered only on those screens, and is sorted above the global ones — "context
   * comes first in the Actions section" (06-qol §1).
   */
  readonly screens?: readonly string[];
  /** Extra terms to match on that are not in the visible label. */
  readonly keywords?: readonly string[];
  /** Rendered right-aligned, e.g. "Ctrl K". Display only — binding lives elsewhere. */
  readonly shortcut?: string;
  readonly icon?: LucideIcon;
  run(): void;
}

type Listener = () => void;

/** Registration order, so equal fuzzy scores resolve to a stable, sensible order. */
const registrationOrder = new Map<string, number>();
let registrations = 0;

const commands = new Map<string, Command>();
const listeners = new Set<Listener>();

/**
 * The snapshot `useSyncExternalStore` reads, cached until the registry actually
 * changes. Returning a fresh array on every call breaks the hook's contract —
 * React compares snapshots by reference, sees a new one every render, and either
 * loops forever or bails out with "The result of getSnapshot should be cached".
 */
let snapshot: readonly Command[] = [];

function notify(): void {
  snapshot = [...commands.values()];
  for (const listener of listeners) listener();
}

/**
 * Adds commands to the palette. Returns a function that removes exactly these
 * again — call it from a module's cleanup so a screen's contextual actions do not
 * outlive the screen.
 *
 * Re-registering the same id replaces it, which is what makes hot module reload
 * and re-mounting a feature safe.
 */
export function registerCommands(toAdd: readonly Command[]): () => void {
  for (const command of toAdd) {
    commands.set(command.id, command);
    if (!registrationOrder.has(command.id)) {
      registrationOrder.set(command.id, registrations);
      registrations += 1;
    }
  }
  notify();

  return () => {
    for (const command of toAdd) {
      // Only remove it if it is still the same registration — a later one wins.
      if (commands.get(command.id) === command) commands.delete(command.id);
    }
    notify();
  };
}

export function getCommands(): readonly Command[] {
  return snapshot;
}

/** For `useSyncExternalStore` in the palette component. */
export function subscribeToCommands(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function commandOrder(id: string): number {
  return registrationOrder.get(id) ?? Number.MAX_SAFE_INTEGER;
}

/**
 * The set the palette is allowed to show: permission-filtered, and narrowed to the
 * current screen's contextual actions plus every global one.
 *
 * Filtering happens here rather than at render, so a command the viewer cannot run
 * never reaches the matcher — a result they cannot open must not appear even for
 * the frame before it is filtered out (06-qol §1).
 */
export function visibleCommands(
  all: readonly Command[],
  session: SessionPayload | null,
  currentScreenId: string | null,
  modules: ModuleState = ALL_MODULES_ON,
): readonly Command[] {
  // Default deny: no session, no commands. The palette is never open in that
  // state, but the rule belongs in the filter rather than in whoever calls it.
  if (!session) return [];

  return all.filter((command) => {
    if (command.permission && !canView(session, command.permission)) return false;
    const module = command.module ?? COMMAND_MODULES[command.id];
    if (module && !isRequirementOn(module, modules)) return false;
    if (command.canSee && !command.canSee(session, modules)) return false;
    if (!command.screens) return true;
    return currentScreenId !== null && command.screens.includes(currentScreenId);
  });
}

/** True when a command is specific to the screen the user is on. */
export function isContextual(command: Command, currentScreenId: string | null): boolean {
  return (
    command.screens !== undefined &&
    currentScreenId !== null &&
    command.screens.includes(currentScreenId)
  );
}

/** Only used by tests, to keep one spec's registrations out of the next one's. */
export function resetCommandsForTest(): void {
  commands.clear();
  registrationOrder.clear();
  registrations = 0;
  notify();
}

/** A test asserting on snapshot identity — the contract the palette depends on. */
export function snapshotIdentityForTest(): readonly Command[] {
  return snapshot;
}
